// ---------------------------------------------------------------------------
// CalendarEvent — generic calendar event type, decoupled from activities table.
// Used by all calendar view components. In the future, events from Google
// Calendar / Outlook will also be mapped to this type.
// ---------------------------------------------------------------------------

export type CalendarEventType =
  | 'call'
  | 'email'
  | 'meeting'
  | 'task'
  | 'note'
  | 'proposal'
  | 'external';

type CalendarEventStatus = 'pending' | 'done' | 'canceled' | 'paused';

export type CalendarEventSource = 'crm' | 'google_calendar' | 'outlook';

export interface CalendarEventParticipant {
  email: string;
  name?: string | null;
  status?: string | null;
}

interface CalendarEventMeta {
  leadId?: string;
  leadName?: string;
  dealId?: string;
  dealTitle?: string;
  companyId?: string;
  companyName?: string;
  assignedTo?: string;
  assigneeName?: string;
  activityId?: string;
  externalEventId?: string;
  conferenceUrl?: string;
  htmlLink?: string;
  /** Descrição da atividade ou do evento no Google */
  description?: string | null;
  /** Link Meet / vídeo (CRM ou sync) */
  meetLink?: string | null;
  /** Participantes (lead, dono, extras do custom_data / Google) */
  participants?: CalendarEventParticipant[];
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  type: CalendarEventType;
  status?: CalendarEventStatus;
  color?: string;
  source: CalendarEventSource;
  meta?: CalendarEventMeta;
}

// ---------------------------------------------------------------------------
// Color mapping — uses design token classes (NOT raw hex/Tailwind colors).
// Pattern adapted from EventBlock.tsx type color mapping.
// ---------------------------------------------------------------------------

const EVENT_TYPE_COLORS: Record<
  CalendarEventType,
  { bg: string; border: string; text: string; darkBg: string; darkBorder: string; darkText: string }
> = {
  meeting: {
    bg: 'bg-primary-100',
    border: 'border-primary-300',
    text: 'text-primary-900',
    darkBg: 'dark:bg-primary-900/30',
    darkBorder: 'dark:border-primary-500',
    darkText: 'dark:text-primary-200',
  },
  call: {
    bg: 'bg-success-100',
    border: 'border-success-300',
    text: 'text-success-900',
    darkBg: 'dark:bg-success-900',
    darkBorder: 'dark:border-success-600',
    darkText: 'dark:text-success-100',
  },
  email: {
    bg: 'bg-secondary-100',
    border: 'border-secondary-300',
    text: 'text-secondary-900',
    darkBg: 'dark:bg-secondary-900',
    darkBorder: 'dark:border-secondary-600',
    darkText: 'dark:text-secondary-100',
  },
  task: {
    bg: 'bg-warning-100',
    border: 'border-warning-300',
    text: 'text-warning-900',
    darkBg: 'dark:bg-warning-900',
    darkBorder: 'dark:border-warning-600',
    darkText: 'dark:text-warning-100',
  },
  proposal: {
    bg: 'bg-accent-100',
    border: 'border-accent-300',
    text: 'text-accent-900',
    darkBg: 'dark:bg-accent-900',
    darkBorder: 'dark:border-accent-600',
    darkText: 'dark:text-accent-100',
  },
  note: {
    bg: 'bg-muted',
    border: 'border-border',
    text: 'text-muted-foreground',
    darkBg: 'dark:bg-muted',
    darkBorder: 'dark:border-border',
    darkText: 'dark:text-muted-foreground',
  },
  external: {
    bg: 'bg-accent-100',
    border: 'border-accent-300',
    text: 'text-accent-900',
    darkBg: 'dark:bg-accent-900',
    darkBorder: 'dark:border-accent-600',
    darkText: 'dark:text-accent-100',
  },
};

export function getEventTypeClasses(type: CalendarEventType, isDone?: boolean): string {
  if (isDone) {
    return 'bg-muted/50 border-muted text-muted-foreground';
  }
  const c = EVENT_TYPE_COLORS[type] ?? EVENT_TYPE_COLORS.note;
  return `${c.bg} ${c.border} ${c.text} ${c.darkBg} ${c.darkBorder} ${c.darkText}`;
}
